'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

function _interopDefault (ex) { return (ex && (typeof ex === 'object') && 'default' in ex) ? ex['default'] : ex; }

var axios = require('axios');
var axios__default = _interopDefault(axios);

const makeRequest = (config) => {
  if (typeof config !== 'object' || Array.isArray(config)) {
    throw new TypeError(`config should be an object but received ${config}`);
  }

  const { body, ...restConfig } = config;
  const {
    token: cancelToken,
    cancel: cancelRequest
  } = axios.CancelToken.source();

  const request = axios__default({
    ...restConfig,
    data: body,
    cancelToken
  }).then(response => {
    // Change "data" to "body" on the response object
    const { data : body, status, headers } = response;

    return {
      status,
      headers,
      ...(body !== undefined && { body }),
      config
    };
  }).catch(axiosErrorResponse => {
    /**
     * The consumer canceling should not result in the
     *  request counting as a failure. Neither a success.
     * Do nothing here.
     */
    if (axios__default.isCancel(axiosErrorResponse)) {
      return;
    }

    const ourErrorResponse = { config };

    if (axiosErrorResponse.response) {
      const { status, headers, data } = axiosErrorResponse.response;
      if (status) { 
        ourErrorResponse.status = status;
      }
      if (headers) {
        ourErrorResponse.headers = headers;
      }
      if (data) {
        ourErrorResponse.body = data;
      }
    }
    
    throw ourErrorResponse;
  });

  return Object.assign(request, {
    // Add the .cancel() method onto the request.
    cancel: cancelRequest
  });
};

var defaultHTTPClientImplementation = /*#__PURE__*/Object.freeze({
  __proto__: null,
  makeRequest: makeRequest
});

/**
 *
 * @param {Response} response
 * @returns {HTTPError}
 */
const responseToError = response => {
  const { status } = response;
  if (status) {
    return new (statusToError[status] || statusToError.default)(response);
  }

  // Network error
  if (response.config) {
    return new NetworkError();
  }

  // JS error
  if (response instanceof Error) {
    return response;
  }
};

class HTTPClientError extends Error {
  constructor (message = 'HTTP-Client Error') {
    super(message);
  }
}

/**
 * Error class representing a failed HTTP request.
 */
class HTTPError extends HTTPClientError {
  constructor(response, defaultMessage = 'HTTP Error') {
    if (response && response.body && typeof response.body.message === 'string') {
      super(response.body.message);
    } else if (response && response.body &&
      Array.isArray(response.body.messages) && response.body.messages.length > 0) {
      // Error message is expecting only 1 message.
      super(response.body.messages[0].message);
    } else {
      super(defaultMessage);
    }

    // Tack the response on.
    Object.assign(this, { response });
  }
}

/**
 * Error class representing an HTTP request that has failed with a "400 - Bad Request" response.
 */
class BadRequestError extends HTTPError {
  constructor(response) {
    super(response, 'Bad Request');
  }
}
// TODO fill in remaining error descriptions
class UnauthorizedError extends HTTPError {
  constructor(response) {
    super(response, 'Unauthorized');
  }
}

class ForbiddenError extends HTTPError {
  constructor(response) {
    super(response, 'Forbidden');
  }
}

class NotFoundError extends HTTPError {
  constructor(response) {
    super(response, 'Not Found');
  }
}

class UnsupportedMediaTypeError extends HTTPError {
  constructor(response) {
    super(response, 'Unsupported Media Type');
  }
}

class InternalServerError extends HTTPError {
  constructor(response) {
    super(response, 'Internal Server Error');
  }
}

class BadGatewayError extends HTTPError {
  constructor(response) {
    super(response, 'Bad Gateway');
  }
}

class ServiceUnavailableError extends HTTPError {
  constructor(response) {
    super(response, 'Service Unavailable');
  }
}

class GatewayTimeoutError extends HTTPError {
  constructor(response) {
    super(response, 'Gateway Timeout');
  }
}

class NetworkError extends Error {
  constructor(message) {
    super(message || 'Network Error');
  }
}

const statusToError = {
  400: BadRequestError,
  401: UnauthorizedError,
  403: ForbiddenError,
  404: NotFoundError,
  415: UnsupportedMediaTypeError,
  500: InternalServerError,
  502: BadGatewayError,
  503: ServiceUnavailableError,
  504: GatewayTimeoutError,
  default: HTTPError
  // Please feel free to add when needed.
};

/**
 * A response object.
 * @typedef Response
 * @property {number} status - The HTTP status code of the response.
 * @property {RequestConfig} config - The RequestConfig used to create the request.
 * @property {Object<string, string>} headers - The response headers.
 * @property {*} body - The body of the response.
 */

const privatesBucket = new WeakMap();
/**
 * This is a private property accessor which is called internally by HTTPClient.
 * @param {HTTPClient} instance - The instance of HTTPClient for which properties will be returned.
 * @returns {object} An object of private properties for the given instance.
 */
const _ = instance => privatesBucket.has(instance) 
  ? privatesBucket.get(instance) 
  : (privatesBucket.set(instance, {}).get(instance));

/**
 * Clones a request-config so that it may be reflected back to the consumer without risk of being arbitrarily mutated.
 * @param {RequestConfig} config - The request-config to be cloned.
 * @returns {RequestConfig} - The cloned request-config.
 */
const cloneRequestConfig = (config) => {
  return {
    ...config,
    headers: {
      ...config.headers
    },
    ...(config.body && { body: JSON.parse(JSON.stringify(config.body)) }) // Naive clone which will work for JSON.
  };
};

/**
 * Returns the default headers that are used if no headers are provided.
 * @returns {Headers}
 */
const getDefaultHeaders = () => {
  return {
    'Accept': 'application/json, text/plain, */*',
    'Content-Type': 'application/json;charset=utf-8'
  };
};

/**
 * Returns the default config that gets extended with any user-provided configuration parameters.
 * @returns {RequestConfig}
 */
const getDefaultConfig = () => {
  return {
    method: 'GET', 
    timeout: 0, // 0 is no timeout
    withCredentials: false,
    headers: getDefaultHeaders()
  };
};

/**
 * Creates a new http-client object used to perform requests.
 * @param {Object} config
 * @param {RequestConfig} [config.baseConfig] - Any part of a RequestConfig to specify as a default
 *  for requests made using this instance of http-client.
 * @param {Implementation} [config.implementation] - An implementation to use
 *  for requests made using this instance of http-client.
 */
const create = ({
  baseConfig = getDefaultConfig(), 
  implementation = defaultHTTPClientImplementation
} = {}) => {
  const httpClient = new HTTPClient(baseConfig, implementation);
  /**
   * Performs an HTTP request, given a RequestConfig, returning a Promise.
   * Also exposes the HTTPClient's alias methods.
   * @type {(config: RequestConfig) => Promise<Response>}
   * @param {RequestConfig} config 
   * @returns {Promise<Response>} 
   */
  const requestor = httpClient.request.bind(httpClient);
  const h = httpClient;

  // Tack on prototype methods for the nice interface of a function with methods on it.
  const ret = Object.assign(requestor, {
    /**
     * Performs an HTTP GET request. Supports an overloaded method where the first param passed 
     *  is the url: api.get('/foo/bar')
     * @type {(urlOrConfig: (String|RequestConfig) => HTTPRequest)}
     * @param {(String|RequestConfig)} urlOrConfig - The url to perform a GET request, or a RequestConfig.
     * @param {RequestConfig} [config] - Additional request options, given the first parameter is the url.
     */
    get: h.get.bind(h), 
    /**
     * Performs an HTTP POST request.
     * @type {(requestConfig: RequestConfig) => HTTPRequest}
     * @param {RequestConfig} requestConfig - The request configuration.
     */
    post: h.post.bind(h), 
    /**
     * Performs an HTTP PUT request.
     * @type {(requestConfig: RequestConfig) => HTTPRequest}
     * @param {RequestConfig} requestConfig - The request configuration.
     */
    put: h.put.bind(h), 
    /**
     * Performs an HTTP PATCH request.
     * @type {(requestConfig: RequestConfig) => HTTPRequest}
     * @param {RequestConfig} requestConfig - The request configuration.
     */
    patch: h.patch.bind(h), 
    /**
     * Performs an HTTP DELETE request.
     * @type {(requestConfig: RequestConfig) => HTTPRequest}
     * @param {RequestConfig} requestConfig - The request configuration.
     */
    delete: h.delete.bind(h),
    /**
     * Adds an interceptor to an http-client's list of interceptors.
     * @type {(interceptor: Interceptor) => void}
     * @param {Interceptor} interceptor - The interceptor to add.
     */
    addInterceptor: h.addInterceptor.bind(h),
    /**
     * Removes an interceptor from an http-client's list of interceptors.
     * @type {(interceptor: Interceptor) => void}
     * @param {Interceptor} interceptor - The interceptor to remove.
     */
    removeInterceptor: h.removeInterceptor.bind(h)
  });

  Object.freeze(ret);
  return ret;
};

/**
 * Adds an interceptor to the list of global interceptors, which are run before any instance-level interceptors.
 * @param {Interceptor} interceptor - An interceptor to add to the list of global interceptors.
 */
const addGlobalInterceptor = interceptor => {
  HTTPClient.addInterceptor(interceptor);
};
/**
 * Removes an interceptor from the list of global interceptors.
 * @param {Interceptor} interceptor - An interceptor to remove from the list of global interceptors.
 */
const removeGlobalInterceptor = interceptor => {
  HTTPClient.removeInterceptor(interceptor);
};

class HTTPClient {
  /**
   * Validates that an interceptor implements at least one lifecylce method, that they are implemented correctly,
   *  and warns if receiving extraneous properties.
   * @param {Interceptor} interceptor - The interceptor to validate. 
   */
  static validateInterceptor ({ request, success, error, ...extraneous } = {}) {
    if (!(request || success || error)) {
      throw new ReferenceError('Interceptor must implement at least one of "request", "success" or "error"');
    }

    for (const acceptedMethod of [request, success, error]) {
      if (acceptedMethod && typeof acceptedMethod !== 'function') {
        throw new TypeError(`Expected interceptor of type "function" but received ${acceptedMethod}.`);
      }
    }
    
    // Warn if passing stuff to constructor which don't make sense.
    if (Object.keys(extraneous).length) {
      // eslint-disable-next-line max-len
      console.warn(`An Interceptor only understands "request", "success", and "error" properties but received additional properties: ${Object.keys(extraneous)}`);
    }
  }
  /**
   * Adds an interceptor to a given collection after validating it.
   * @param {Interceptor} interceptor - The interceptor to add.
   * @param {Interceptor[]} [interceptorsCollection=HTTPClient.interceptors]
   */
  static addInterceptor (interceptor, interceptorsCollection = HTTPClient.interceptors) {
    this.validateInterceptor(interceptor); 
    if (interceptorsCollection.includes(interceptor)) {
      console.warn('Duplicate interceptor added.');
    }
    interceptorsCollection.push(interceptor);
  }
  /**
   * Removes an interceptor from a given collection.
   * @param {Interceptor} interceptor - The interceptor to add.
   * @param {Interceptor[]} [interceptorsCollection=HTTPClient.interceptors]
   */
  static removeInterceptor (interceptor, interceptorsCollection = HTTPClient.interceptors) {
    const indexOfInterceptor = interceptorsCollection.indexOf(interceptor);
    if (indexOfInterceptor === -1) {
      console.warn('Tried to remove interceptor but it did not exist.');
    } else {
      interceptorsCollection.splice(indexOfInterceptor, 1);
    }
  }
  /**
   * Creates a new HTTPClient instance. 
   * @param {RequestConfig} config
   * @param {Implementation} [implementation]
   */
  constructor(config, implementation) {
    // Assign privates.
    Object.assign(_(this), {
      config,
      implementation,
      interceptors: [],
      /** 
       * Runs the given lifecycle method of all global interceptors and this instance's interceptors, 
       *  in order, passing in the given object.
       * @param {string} type - "request", "success", or "error".
       * @param {*} input - The input to be piped through the interceptors.
       */
      runInterceptors: (type, input) => {
        const interceptors = HTTPClient.interceptors.concat(_(this).interceptors);
        return interceptors.reduce((_input, interceptor) => {
          const interceptorMethod = interceptor[type];
          return interceptorMethod ? interceptorMethod(_input) : _input;
        }, input);
      }
    });

    this.apiWithMethod = this.apiWithMethod.bind(this);
  }
  /**
   * Adds an interceptor to an http-client's list of interceptors.
   * @param {Interceptor} interceptor - The interceptor to add.
   */
  addInterceptor (interceptor) {
    HTTPClient.addInterceptor(interceptor, _(this).interceptors);
  }
  /**
   * Removes an interceptor from an http-client's list of interceptors.
   * @param {Interceptor} interceptor - The interceptor to remove.
   */
  removeInterceptor (interceptor) {
    HTTPClient.removeInterceptor(interceptor, _(this).interceptors);
  }
  /**
   * Creates an alias method with the given HTTP method.
   * @param {String} method - The HTTP method to bind to the new request function.
   * @returns {Function} - A new function with a default HTTP method which accepts 
   *  a RequestConfig and returns a Request.
   */
  apiWithMethod (method) {
    return config => this.request({...config, method});
  }
  /*
   * Alias methods for frequently used HTTP verbs.
   */
  /**
   * Performs an HTTP GET request.
   * Because it's such a common use-case for GET requests, support an overloaded method 
   * where the first param passed is the url: api.get('/foo/bar')
   * @param {(String|RequestConfig)} urlOrConfig - The url to perform a GET request, or a RequestConfig.
   * @param {RequestConfig} [config] - Additional request options, given the first parameter is the url.
   * @returns {HTTPRequest}
   */
  get (urlOrConfig, config) {
    const apiWithGet = this.apiWithMethod('GET');

    return (typeof urlOrConfig === 'string')
      ? apiWithGet({ url: urlOrConfig, ...config })
      : apiWithGet(urlOrConfig);
  }
  /**
   * Performs an HTTP POST request.
   * @param {RequestConfig} args - The request configuration.
   * @returns {HTTPRequest}
   */
  post (...args) {
    return this.apiWithMethod('POST')(...args);
  }
  /**
   * Performs an HTTP PUT request.
   * @param {RequestConfig} args - The request configuration.
   * @returns {HTTPRequest}
   */
  put (...args) { 
    return this.apiWithMethod('PUT')(...args);
  }
  /**
   * Performs an HTTP DELETE request.
   * @param {RequestConfig} args - The request configuration.
   * @returns {HTTPRequest}
   */
  delete (...args) { 
    return this.apiWithMethod('DELETE')(...args);
  }
  /**
   * Performs an HTTP PATCH request.
   * @param {RequestConfig} args - The request configuration.
   * @returns {HTTPRequest}
   */
  patch (...args) {
    return this.apiWithMethod('PATCH')(...args);
  }
  /**
   * Performs an HTTP request.
   * @param {RequestConfig} requestConfig - The configuration object to use for the request.
   * @returns {HTTPRequest}
   */
  request (requestConfig) {
    const processedRequestConfig = _(this).runInterceptors('request', requestConfig);
    /** 
     * implementationOptions are props unrecognized by http-client wrapper and will get passed through to the 
     *  underlying implementation being used. 
     */
    const { url, method, body, headers, timeout, withCredentials, ...implementationOptions } = {
      // Merge the requestConfig into the config which was set using the constructor.
      ..._(this).config,
      ...processedRequestConfig
    };

    if (typeof url !== 'string') {
      throw new TypeError(`Expected url to be a string but instead got ${url}`);
    }
    
    // 1. Clone the request-config so consumers can't mutate the original headers or body.
    const clonedRequestConfig = cloneRequestConfig({ 
      url, method, ...(body && { body }), headers, timeout, withCredentials, ...implementationOptions
    });

    // 2. Make the request.
    const request = new Promise((resolve, reject) => {

      _(this).implementation.makeRequest(clonedRequestConfig)
        .then(response => {
          resolve(response);
        })
        .catch(reject);
    });

    // 3. Annotate the request object with copies of its configuration settings, for reflection.  
    // 4. Map error responses to HTTPError objects
    // 5. Return the request.
    return Object.assign(request
      .then(response => {
        return _(this).runInterceptors('success', response);
      })
      .catch(response => {
        const error = responseToError(response);
        const processedError = _(this).runInterceptors('error', error);
        return (processedError instanceof Error) ? Promise.reject(processedError) : processedError;
      }), clonedRequestConfig, {
      // Hoist any properties added to request by implementation, eg "cancel".
      ...request
    });
  }
}

HTTPClient.interceptors = [];

/**
 * In some ways the HTTPClient singleton.
 */
var httpClient = create();

/**
 * An HTTPClient implementation.
 * @typedef {Object} Implementation
 * @property {function(RequestConfig): HTTPRequest} makeRequest - Will be called with the RequestConfig and should 
 *  return a HTTPRequest.
 */

/**
 * A configuration object used to create a Request.
 * @typedef {Object} RequestConfig
 * @property {string} url - The endpoint uri.
 * @property {string} [method=GET] - the HTTP request method.
 * @property {*} [body] - the payload of a POST, PUT, or PATCH.
 * @property {Object<string, string>} [headers=getDefaultHeaders()] - HTTP headers to set on the request.
 * @property {number} [timeout=0] - time (in ms) until the request times out. 0 means never time out.
 * @property {boolean} [withCredentials=false] - indicates whether or not cross-site Access-Control 
 *  requests should be made using credentials.
 */

/**
 * An HTTP Request.
 * @typedef {Promise<Response|Error>} HTTPRequest
 */

/**
 * A response object.
 * @typedef Response
 * @property {number} status - The HTTP status code of the response.
 * @property {RequestConfig} config - The RequestConfig used to create the request.
 * @property {Object<string, string>} headers - The response headers.
 * @property {*} body - The body of the response.
 */

/* 
  * An interceptor which acts on an http-request at some point during its lifecycle.
  * @typedef Interceptor
  * @property {function(RequestConfig): RequestConfig} request - A function to run before the request gets sent, 
  *   which receives the RequestConfig as an argument and should return a valid RequestConfig.
  * @property {function(Response): *} success - A function to run when a request is successful. Whatever this function 
  *   returns will be the input of the next interceptor or, if this is the last, any request-promise resolvers.
  * @property {function(Error): *} error - A function to run when a request results in an error. Whatever this function 
  *   returns will be the input of the next interceptor or, if this is the last, any request-promise resolvers.
  */

var toStr = Object.prototype.toString;

var isArguments = function isArguments(value) {
	var str = toStr.call(value);
	var isArgs = str === '[object Arguments]';
	if (!isArgs) {
		isArgs = str !== '[object Array]' &&
			value !== null &&
			typeof value === 'object' &&
			typeof value.length === 'number' &&
			value.length >= 0 &&
			toStr.call(value.callee) === '[object Function]';
	}
	return isArgs;
};

var keysShim;
if (!Object.keys) {
	// modified from https://github.com/es-shims/es5-shim
	var has = Object.prototype.hasOwnProperty;
	var toStr$1 = Object.prototype.toString;
	var isArgs = isArguments; // eslint-disable-line global-require
	var isEnumerable = Object.prototype.propertyIsEnumerable;
	var hasDontEnumBug = !isEnumerable.call({ toString: null }, 'toString');
	var hasProtoEnumBug = isEnumerable.call(function () {}, 'prototype');
	var dontEnums = [
		'toString',
		'toLocaleString',
		'valueOf',
		'hasOwnProperty',
		'isPrototypeOf',
		'propertyIsEnumerable',
		'constructor'
	];
	var equalsConstructorPrototype = function (o) {
		var ctor = o.constructor;
		return ctor && ctor.prototype === o;
	};
	var excludedKeys = {
		$applicationCache: true,
		$console: true,
		$external: true,
		$frame: true,
		$frameElement: true,
		$frames: true,
		$innerHeight: true,
		$innerWidth: true,
		$onmozfullscreenchange: true,
		$onmozfullscreenerror: true,
		$outerHeight: true,
		$outerWidth: true,
		$pageXOffset: true,
		$pageYOffset: true,
		$parent: true,
		$scrollLeft: true,
		$scrollTop: true,
		$scrollX: true,
		$scrollY: true,
		$self: true,
		$webkitIndexedDB: true,
		$webkitStorageInfo: true,
		$window: true
	};
	var hasAutomationEqualityBug = (function () {
		/* global window */
		if (typeof window === 'undefined') { return false; }
		for (var k in window) {
			try {
				if (!excludedKeys['$' + k] && has.call(window, k) && window[k] !== null && typeof window[k] === 'object') {
					try {
						equalsConstructorPrototype(window[k]);
					} catch (e) {
						return true;
					}
				}
			} catch (e) {
				return true;
			}
		}
		return false;
	}());
	var equalsConstructorPrototypeIfNotBuggy = function (o) {
		/* global window */
		if (typeof window === 'undefined' || !hasAutomationEqualityBug) {
			return equalsConstructorPrototype(o);
		}
		try {
			return equalsConstructorPrototype(o);
		} catch (e) {
			return false;
		}
	};

	keysShim = function keys(object) {
		var isObject = object !== null && typeof object === 'object';
		var isFunction = toStr$1.call(object) === '[object Function]';
		var isArguments = isArgs(object);
		var isString = isObject && toStr$1.call(object) === '[object String]';
		var theKeys = [];

		if (!isObject && !isFunction && !isArguments) {
			throw new TypeError('Object.keys called on a non-object');
		}

		var skipProto = hasProtoEnumBug && isFunction;
		if (isString && object.length > 0 && !has.call(object, 0)) {
			for (var i = 0; i < object.length; ++i) {
				theKeys.push(String(i));
			}
		}

		if (isArguments && object.length > 0) {
			for (var j = 0; j < object.length; ++j) {
				theKeys.push(String(j));
			}
		} else {
			for (var name in object) {
				if (!(skipProto && name === 'prototype') && has.call(object, name)) {
					theKeys.push(String(name));
				}
			}
		}

		if (hasDontEnumBug) {
			var skipConstructor = equalsConstructorPrototypeIfNotBuggy(object);

			for (var k = 0; k < dontEnums.length; ++k) {
				if (!(skipConstructor && dontEnums[k] === 'constructor') && has.call(object, dontEnums[k])) {
					theKeys.push(dontEnums[k]);
				}
			}
		}
		return theKeys;
	};
}
var implementation = keysShim;

var slice = Array.prototype.slice;


var origKeys = Object.keys;
var keysShim$1 = origKeys ? function keys(o) { return origKeys(o); } : implementation;

var originalKeys = Object.keys;

keysShim$1.shim = function shimObjectKeys() {
	if (Object.keys) {
		var keysWorksWithArguments = (function () {
			// Safari 5.0 bug
			var args = Object.keys(arguments);
			return args && args.length === arguments.length;
		}(1, 2));
		if (!keysWorksWithArguments) {
			Object.keys = function keys(object) { // eslint-disable-line func-name-matching
				if (isArguments(object)) {
					return originalKeys(slice.call(object));
				}
				return originalKeys(object);
			};
		}
	} else {
		Object.keys = keysShim$1;
	}
	return Object.keys || keysShim$1;
};

var objectKeys = keysShim$1;

var hasToStringTag = typeof Symbol === 'function' && typeof Symbol.toStringTag === 'symbol';
var toStr$2 = Object.prototype.toString;

var isStandardArguments = function isArguments(value) {
	if (hasToStringTag && value && typeof value === 'object' && Symbol.toStringTag in value) {
		return false;
	}
	return toStr$2.call(value) === '[object Arguments]';
};

var isLegacyArguments = function isArguments(value) {
	if (isStandardArguments(value)) {
		return true;
	}
	return value !== null &&
		typeof value === 'object' &&
		typeof value.length === 'number' &&
		value.length >= 0 &&
		toStr$2.call(value) !== '[object Array]' &&
		toStr$2.call(value.callee) === '[object Function]';
};

var supportsStandardArguments = (function () {
	return isStandardArguments(arguments);
}());

isStandardArguments.isLegacyArguments = isLegacyArguments; // for tests

var isArguments$1 = supportsStandardArguments ? isStandardArguments : isLegacyArguments;

/* https://people.mozilla.org/~jorendorff/es6-draft.html#sec-object.is */

var NumberIsNaN = function (value) {
	return value !== value;
};

var objectIs = function is(a, b) {
	if (a === 0 && b === 0) {
		return 1 / a === 1 / b;
	} else if (a === b) {
		return true;
	} else if (NumberIsNaN(a) && NumberIsNaN(b)) {
		return true;
	}
	return false;
};

/* eslint no-invalid-this: 1 */

var ERROR_MESSAGE = 'Function.prototype.bind called on incompatible ';
var slice$1 = Array.prototype.slice;
var toStr$3 = Object.prototype.toString;
var funcType = '[object Function]';

var implementation$1 = function bind(that) {
    var target = this;
    if (typeof target !== 'function' || toStr$3.call(target) !== funcType) {
        throw new TypeError(ERROR_MESSAGE + target);
    }
    var args = slice$1.call(arguments, 1);

    var bound;
    var binder = function () {
        if (this instanceof bound) {
            var result = target.apply(
                this,
                args.concat(slice$1.call(arguments))
            );
            if (Object(result) === result) {
                return result;
            }
            return this;
        } else {
            return target.apply(
                that,
                args.concat(slice$1.call(arguments))
            );
        }
    };

    var boundLength = Math.max(0, target.length - args.length);
    var boundArgs = [];
    for (var i = 0; i < boundLength; i++) {
        boundArgs.push('$' + i);
    }

    bound = Function('binder', 'return function (' + boundArgs.join(',') + '){ return binder.apply(this,arguments); }')(binder);

    if (target.prototype) {
        var Empty = function Empty() {};
        Empty.prototype = target.prototype;
        bound.prototype = new Empty();
        Empty.prototype = null;
    }

    return bound;
};

var functionBind = Function.prototype.bind || implementation$1;

var src = functionBind.call(Function.call, Object.prototype.hasOwnProperty);

var regexExec = RegExp.prototype.exec;
var gOPD = Object.getOwnPropertyDescriptor;

var tryRegexExecCall = function tryRegexExec(value) {
	try {
		var lastIndex = value.lastIndex;
		value.lastIndex = 0;

		regexExec.call(value);
		return true;
	} catch (e) {
		return false;
	} finally {
		value.lastIndex = lastIndex;
	}
};
var toStr$4 = Object.prototype.toString;
var regexClass = '[object RegExp]';
var hasToStringTag$1 = typeof Symbol === 'function' && typeof Symbol.toStringTag === 'symbol';

var isRegex = function isRegex(value) {
	if (!value || typeof value !== 'object') {
		return false;
	}
	if (!hasToStringTag$1) {
		return toStr$4.call(value) === regexClass;
	}

	var descriptor = gOPD(value, 'lastIndex');
	var hasLastIndexDataProperty = descriptor && src(descriptor, 'value');
	if (!hasLastIndexDataProperty) {
		return false;
	}

	return tryRegexExecCall(value);
};

var hasSymbols = typeof Symbol === 'function' && typeof Symbol('foo') === 'symbol';

var toStr$5 = Object.prototype.toString;
var concat = Array.prototype.concat;
var origDefineProperty = Object.defineProperty;

var isFunction = function (fn) {
	return typeof fn === 'function' && toStr$5.call(fn) === '[object Function]';
};

var arePropertyDescriptorsSupported = function () {
	var obj = {};
	try {
		origDefineProperty(obj, 'x', { enumerable: false, value: obj });
		// eslint-disable-next-line no-unused-vars, no-restricted-syntax
		for (var _ in obj) { // jscs:ignore disallowUnusedVariables
			return false;
		}
		return obj.x === obj;
	} catch (e) { /* this is IE 8. */
		return false;
	}
};
var supportsDescriptors = origDefineProperty && arePropertyDescriptorsSupported();

var defineProperty = function (object, name, value, predicate) {
	if (name in object && (!isFunction(predicate) || !predicate())) {
		return;
	}
	if (supportsDescriptors) {
		origDefineProperty(object, name, {
			configurable: true,
			enumerable: false,
			value: value,
			writable: true
		});
	} else {
		object[name] = value;
	}
};

var defineProperties = function (object, map) {
	var predicates = arguments.length > 2 ? arguments[2] : {};
	var props = objectKeys(map);
	if (hasSymbols) {
		props = concat.call(props, Object.getOwnPropertySymbols(map));
	}
	for (var i = 0; i < props.length; i += 1) {
		defineProperty(object, props[i], map[props[i]], predicates[props[i]]);
	}
};

defineProperties.supportsDescriptors = !!supportsDescriptors;

var defineProperties_1 = defineProperties;

var toObject = Object;
var TypeErr = TypeError;

var implementation$2 = function flags() {
	if (this != null && this !== toObject(this)) {
		throw new TypeErr('RegExp.prototype.flags getter called on non-object');
	}
	var result = '';
	if (this.global) {
		result += 'g';
	}
	if (this.ignoreCase) {
		result += 'i';
	}
	if (this.multiline) {
		result += 'm';
	}
	if (this.dotAll) {
		result += 's';
	}
	if (this.unicode) {
		result += 'u';
	}
	if (this.sticky) {
		result += 'y';
	}
	return result;
};

var supportsDescriptors$1 = defineProperties_1.supportsDescriptors;
var gOPD$1 = Object.getOwnPropertyDescriptor;
var TypeErr$1 = TypeError;

var polyfill = function getPolyfill() {
	if (!supportsDescriptors$1) {
		throw new TypeErr$1('RegExp.prototype.flags requires a true ES5 environment that supports property descriptors');
	}
	if (/a/mig.flags === 'gim') {
		var descriptor = gOPD$1(RegExp.prototype, 'flags');
		if (descriptor && typeof descriptor.get === 'function' && typeof (/a/).dotAll === 'boolean') {
			return descriptor.get;
		}
	}
	return implementation$2;
};

var supportsDescriptors$2 = defineProperties_1.supportsDescriptors;

var gOPD$2 = Object.getOwnPropertyDescriptor;
var defineProperty$1 = Object.defineProperty;
var TypeErr$2 = TypeError;
var getProto = Object.getPrototypeOf;
var regex = /a/;

var shim = function shimFlags() {
	if (!supportsDescriptors$2 || !getProto) {
		throw new TypeErr$2('RegExp.prototype.flags requires a true ES5 environment that supports property descriptors');
	}
	var polyfill$1 = polyfill();
	var proto = getProto(regex);
	var descriptor = gOPD$2(proto, 'flags');
	if (!descriptor || descriptor.get !== polyfill$1) {
		defineProperty$1(proto, 'flags', {
			configurable: true,
			enumerable: false,
			get: polyfill$1
		});
	}
	return polyfill$1;
};

var flagsBound = Function.call.bind(implementation$2);

defineProperties_1(flagsBound, {
	getPolyfill: polyfill,
	implementation: implementation$2,
	shim: shim
});

var regexp_prototype_flags = flagsBound;

var getDay = Date.prototype.getDay;
var tryDateObject = function tryDateObject(value) {
	try {
		getDay.call(value);
		return true;
	} catch (e) {
		return false;
	}
};

var toStr$6 = Object.prototype.toString;
var dateClass = '[object Date]';
var hasToStringTag$2 = typeof Symbol === 'function' && typeof Symbol.toStringTag === 'symbol';

var isDateObject = function isDateObject(value) {
	if (typeof value !== 'object' || value === null) { return false; }
	return hasToStringTag$2 ? tryDateObject(value) : toStr$6.call(value) === dateClass;
};

var getTime = Date.prototype.getTime;

function deepEqual(actual, expected, options) {
  var opts = options || {};

  // 7.1. All identical values are equivalent, as determined by ===.
  if (opts.strict ? objectIs(actual, expected) : actual === expected) {
    return true;
  }

  // 7.3. Other pairs that do not both pass typeof value == 'object', equivalence is determined by ==.
  if (!actual || !expected || (typeof actual !== 'object' && typeof expected !== 'object')) {
    return opts.strict ? objectIs(actual, expected) : actual == expected;
  }

  /*
   * 7.4. For all other Object pairs, including Array objects, equivalence is
   * determined by having the same number of owned properties (as verified
   * with Object.prototype.hasOwnProperty.call), the same set of keys
   * (although not necessarily the same order), equivalent values for every
   * corresponding key, and an identical 'prototype' property. Note: this
   * accounts for both named and indexed properties on Arrays.
   */
  // eslint-disable-next-line no-use-before-define
  return objEquiv(actual, expected, opts);
}

function isUndefinedOrNull(value) {
  return value === null || value === undefined;
}

function isBuffer(x) {
  if (!x || typeof x !== 'object' || typeof x.length !== 'number') {
    return false;
  }
  if (typeof x.copy !== 'function' || typeof x.slice !== 'function') {
    return false;
  }
  if (x.length > 0 && typeof x[0] !== 'number') {
    return false;
  }
  return true;
}

function objEquiv(a, b, opts) {
  /* eslint max-statements: [2, 50] */
  var i, key;
  if (typeof a !== typeof b) { return false; }
  if (isUndefinedOrNull(a) || isUndefinedOrNull(b)) { return false; }

  // an identical 'prototype' property.
  if (a.prototype !== b.prototype) { return false; }

  if (isArguments$1(a) !== isArguments$1(b)) { return false; }

  var aIsRegex = isRegex(a);
  var bIsRegex = isRegex(b);
  if (aIsRegex !== bIsRegex) { return false; }
  if (aIsRegex || bIsRegex) {
    return a.source === b.source && regexp_prototype_flags(a) === regexp_prototype_flags(b);
  }

  if (isDateObject(a) && isDateObject(b)) {
    return getTime.call(a) === getTime.call(b);
  }

  var aIsBuffer = isBuffer(a);
  var bIsBuffer = isBuffer(b);
  if (aIsBuffer !== bIsBuffer) { return false; }
  if (aIsBuffer || bIsBuffer) { // && would work too, because both are true or both false here
    if (a.length !== b.length) { return false; }
    for (i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) { return false; }
    }
    return true;
  }

  if (typeof a !== typeof b) { return false; }

  try {
    var ka = objectKeys(a);
    var kb = objectKeys(b);
  } catch (e) { // happens when one is a string literal and the other isn't
    return false;
  }
  // having the same number of owned properties (keys incorporates hasOwnProperty)
  if (ka.length !== kb.length) { return false; }

  // the same set of keys (although not necessarily the same order),
  ka.sort();
  kb.sort();
  // ~~~cheap key test
  for (i = ka.length - 1; i >= 0; i--) {
    if (ka[i] != kb[i]) { return false; }
  }
  // equivalent values for every corresponding key, and ~~~possibly expensive deep test
  for (i = ka.length - 1; i >= 0; i--) {
    key = ka[i];
    if (!deepEqual(a[key], b[key], opts)) { return false; }
  }

  return true;
}

var deepEqual_1 = deepEqual;

function isEqual(a, b) {
  return deepEqual_1(a, b, { strict: true });
}

// < 0.13.0 will not have default headers set on a custom instance
var rejectWithError = !!axios__default.create().defaults.headers;

function find(array, predicate) {
  var length = array.length;
  for (var i = 0; i < length; i++) {
    var value = array[i];
    if (predicate(value)) return value;
  }
}

function combineUrls(baseURL, url) {
  if (baseURL) {
    return baseURL.replace(/\/+$/, '') + '/' + url.replace(/^\/+/, '');
  }

  return url;
}

function findHandler(handlers, method, url, body, parameters, headers, baseURL) {
  return find(handlers[method.toLowerCase()], function(handler) {
    if (typeof handler[0] === 'string') {
      return (isUrlMatching(url, handler[0]) || isUrlMatching(combineUrls(baseURL, url), handler[0])) && isBodyOrParametersMatching(method, body, parameters, handler[1])  && isRequestHeadersMatching(headers, handler[2]);
    } else if (handler[0] instanceof RegExp) {
      return (handler[0].test(url) || handler[0].test(combineUrls(baseURL, url))) && isBodyOrParametersMatching(method, body, parameters, handler[1]) && isRequestHeadersMatching(headers, handler[2]);
    }
  });
}

function isUrlMatching(url, required) {
  var noSlashUrl = url[0] === '/' ? url.substr(1) : url;
  var noSlashRequired = required[0] === '/' ? required.substr(1) : required;
  return (noSlashUrl === noSlashRequired);
}

function isRequestHeadersMatching(requestHeaders, required) {
  if (required === undefined) return true;
  return isEqual(requestHeaders, required);
}

function isBodyOrParametersMatching(method, body, parameters, required) {
  var allowedParamsMethods = ['delete', 'get', 'head', 'options'];
  if (allowedParamsMethods.indexOf(method.toLowerCase()) >= 0 ) {
    var params = required ? required.params : undefined;
    return isParametersMatching(parameters, params);
  } else {
    return isBodyMatching(body, required);
  }
}

function isParametersMatching(parameters, required) {
  if (required === undefined) return true;

  return isEqual(parameters, required);
}

function isBodyMatching(body, requiredBody) {
  if (requiredBody === undefined) {
    return true;
  }
  var parsedBody;
  try {
    parsedBody = JSON.parse(body);
  } catch (e) { }
  return parsedBody ? isEqual(parsedBody, requiredBody) : isEqual(body, requiredBody);
}

function purgeIfReplyOnce(mock, handler) {
  Object.keys(mock.handlers).forEach(function(key) {
    var index = mock.handlers[key].indexOf(handler);
    if (index > -1) {
      mock.handlers[key].splice(index, 1);
    }
  });
}

function settle(resolve, reject, response, delay) {
  if (delay > 0) {
    setTimeout(function() {
      settle(resolve, reject, response);
    }, delay);
    return;
  }

  if (response.config && response.config.validateStatus) {
    response.config.validateStatus(response.status)
      ? resolve(response)
      : reject(createErrorResponse(
        'Request failed with status code ' + response.status,
        response.config,
        response
      ));
    return;
  }

  // Support for axios < 0.11
  if (response.status >= 200 && response.status < 300) {
    resolve(response);
  } else {
    reject(response);
  }
}

function createErrorResponse(message, config, response) {
  // Support for axios < 0.13.0
  if (!rejectWithError) return response;

  var error = new Error(message);
  error.config = config;
  error.response = response;
  return error;
}

function isSimpleObject(value) {
  return value !== null && value !== undefined && value.toString() === '[object Object]';
}

var utils = {
  find: find,
  findHandler: findHandler,
  isSimpleObject: isSimpleObject,
  purgeIfReplyOnce: purgeIfReplyOnce,
  settle: settle
};

function makeResponse(result, config) {
  return {
    status: result[0],
    data: utils.isSimpleObject(result[1]) ? JSON.parse(JSON.stringify(result[1])) : result[1],
    headers: result[2],
    config: config
  };
}

function handleRequest(mockAdapter, resolve, reject, config) {
  var url = config.url;
  if (config.baseURL && config.url.substr(0, config.baseURL.length) === config.baseURL) {
    url = config.url.slice(config.baseURL ? config.baseURL.length : 0);
  }

  delete config.adapter;
  mockAdapter.history[config.method].push(config);

  var handler = utils.findHandler(
    mockAdapter.handlers,
    config.method,
    url,
    config.data,
    config.params,
    config.headers,
    config.baseURL
  );

  if (handler) {
    if (handler.length === 7) {
      utils.purgeIfReplyOnce(mockAdapter, handler);
    }

    if (handler.length === 2) {
      // passThrough handler
      mockAdapter.originalAdapter(config).then(resolve, reject);
    } else if (typeof handler[3] !== 'function') {
      utils.settle(
        resolve,
        reject,
        makeResponse(handler.slice(3), config),
        mockAdapter.delayResponse
      );
    } else {
      var result = handler[3](config);
      // TODO throw a sane exception when return value is incorrect
      if (typeof result.then !== 'function') {
        utils.settle(resolve, reject, makeResponse(result, config), mockAdapter.delayResponse);
      } else {
        result.then(
          function(result) {
            if (result.config && result.status) {
              utils.settle(resolve, reject, makeResponse([result.status, result.data, result.headers], result.config), 0);
            } else {
              utils.settle(resolve, reject, makeResponse(result, config), mockAdapter.delayResponse);
            }
          },
          function(error) {
            if (mockAdapter.delayResponse > 0) {
              setTimeout(function() {
                reject(error);
              }, mockAdapter.delayResponse);
            } else {
              reject(error);
            }
          }
        );
      }
    }
  } else {
    // handler not found
    utils.settle(
      resolve,
      reject,
      {
        status: 404,
        config: config
      },
      mockAdapter.delayResponse
    );
  }
}

var handle_request = handleRequest;

var VERBS = ['get', 'post', 'head', 'delete', 'patch', 'put', 'options', 'list'];

function adapter() {
  return function(config) {
    var mockAdapter = this;
    // axios >= 0.13.0 only passes the config and expects a promise to be
    // returned. axios < 0.13.0 passes (config, resolve, reject).
    if (arguments.length === 3) {
      handle_request(mockAdapter, arguments[0], arguments[1], arguments[2]);
    } else {
      return new Promise(function(resolve, reject) {
        handle_request(mockAdapter, resolve, reject, config);
      });
    }
  }.bind(this);
}

function getVerbObject() {
  return VERBS.reduce(function(accumulator, verb) {
    accumulator[verb] = [];
    return accumulator;
  }, {});
}

function reset() {
  resetHandlers.call(this);
  resetHistory.call(this);
}

function resetHandlers() {
  this.handlers = getVerbObject();
}

function resetHistory() {
  this.history = getVerbObject();
}

function MockAdapter(axiosInstance, options) {
  reset.call(this);

  if (axiosInstance) {
    this.axiosInstance = axiosInstance;
    this.originalAdapter = axiosInstance.defaults.adapter;
    this.delayResponse = options && options.delayResponse > 0 ? options.delayResponse : null;
    axiosInstance.defaults.adapter = this.adapter.call(this);
  }
}

MockAdapter.prototype.adapter = adapter;

MockAdapter.prototype.restore = function restore() {
  if (this.axiosInstance) {
    this.axiosInstance.defaults.adapter = this.originalAdapter;
  }
};

MockAdapter.prototype.reset = reset;
MockAdapter.prototype.resetHandlers = resetHandlers;
MockAdapter.prototype.resetHistory = resetHistory;

VERBS.concat('any').forEach(function(method) {
  var methodName = 'on' + method.charAt(0).toUpperCase() + method.slice(1);
  MockAdapter.prototype[methodName] = function(matcher, body, requestHeaders) {
    var _this = this;
    var matcher = matcher === undefined ? /.*/ : matcher;

    function reply(code, response, headers) {
      var handler = [matcher, body, requestHeaders, code, response, headers];
      addHandler(method, _this.handlers, handler);
      return _this;
    }

    function replyOnce(code, response, headers) {
      var handler = [matcher, body, requestHeaders, code, response, headers, true];
      addHandler(method, _this.handlers, handler);
      return _this;
    }

    return {
      reply: reply,

      replyOnce: replyOnce,

      passThrough: function passThrough() {
        var handler = [matcher, body];
        addHandler(method, _this.handlers, handler);
        return _this;
      },

      networkError: function() {
        reply(function(config) {
          var error = new Error('Network Error');
          error.config = config;
          return Promise.reject(error);
        });
      },

      networkErrorOnce: function() {
        replyOnce(function(config) {
          var error = new Error('Network Error');
          error.config = config;
          return Promise.reject(error);
        });
      },

      timeout: function() {
        reply(function(config) {
          var error = new Error('timeout of ' + config.timeout + 'ms exceeded');
          error.config = config;
          error.code = 'ECONNABORTED';
          return Promise.reject(error);
        });
      },

      timeoutOnce: function() {
        replyOnce(function(config) {
          var error = new Error('timeout of ' + config.timeout + 'ms exceeded');
          error.config = config;
          error.code = 'ECONNABORTED';
          return Promise.reject(error);
        });
      }
    };
  };
});

function findInHandlers(method, handlers, handler) {
  var index = -1;
  for (var i = 0; i < handlers[method].length; i += 1) {
    var item = handlers[method][i];
    var isReplyOnce = item.length === 7;
    var comparePaths = item[0] instanceof RegExp && handler[0] instanceof RegExp
      ? String(item[0]) === String(handler[0])
      : item[0] === handler[0];
    var isSame = (
      comparePaths &&
      deepEqual_1(item[1], handler[1], { strict: true }) &&
      deepEqual_1(item[2], handler[2], { strict: true })
    );
    if (isSame && !isReplyOnce) {
      index =  i;
    }
  }
  return index;
}

function addHandler(method, handlers, handler) {
  if (method === 'any') {
    VERBS.forEach(function(verb) {
      handlers[verb].push(handler);
    });
  } else {
    var indexOfExistingHandler = findInHandlers(method, handlers, handler);
    if (indexOfExistingHandler > -1 && handler.length < 7) {
      handlers[method].splice(indexOfExistingHandler, 1, handler);
    } else {
      handlers[method].push(handler);
    }
  }
}

var src$1 = MockAdapter;
var _default = MockAdapter;
src$1.default = _default;

let mockAxios;

const mock = (config = {}) => {
  mockAxios = new src$1(axios__default);

  if (config === false) {
    mockAxios.restore();
  } else {
    Object.keys(config).forEach(endpoint => {
      const {GET, POST} = config[endpoint];
      GET && mockAxios.onGet(endpoint).reply(GET.status, GET.body, GET.headers || {});
      POST && mockAxios.onPost(endpoint).reply(POST.status, POST.body, POST.headers || {});
    });
  }
};

const mockNetworkError = () => {
  mockAxios.reset();
  mockAxios.onAny().networkErrorOnce();
};

const mockTimeout = () => {
  mockAxios.reset();
  mockAxios.onAny().timeoutOnce();
};

exports.BadGatewayError = BadGatewayError;
exports.BadRequestError = BadRequestError;
exports.ForbiddenError = ForbiddenError;
exports.GatewayTimeoutError = GatewayTimeoutError;
exports.HTTPError = HTTPError;
exports.InternalServerError = InternalServerError;
exports.NetworkError = NetworkError;
exports.NotFoundError = NotFoundError;
exports.ServiceUnavailableError = ServiceUnavailableError;
exports.UnauthorizedError = UnauthorizedError;
exports.UnsupportedMediaTypeError = UnsupportedMediaTypeError;
exports.addGlobalInterceptor = addGlobalInterceptor;
exports.cloneRequestConfig = cloneRequestConfig;
exports.create = create;
exports.default = httpClient;
exports.getDefaultConfig = getDefaultConfig;
exports.getDefaultHeaders = getDefaultHeaders;
exports.mock = mock;
exports.mockNetworkError = mockNetworkError;
exports.mockTimeout = mockTimeout;
exports.removeGlobalInterceptor = removeGlobalInterceptor;
